// cycle 011 (Matt 2026-10-31: "basically a huge, sparse, lifeless world"; research/2026-10-31-density-scale-doors.md R5, P5):
// the street's small life, drawn on this client only (no server, no network): pigeons in small flocks on the sidewalks that
// peck and bob and go up in a clatter when you or one of the turned come within 3.5 m, and litter the wind pushes along the
// ground in tumbling hops. Motion carries the eye (Game Developer's "events": birds taking flight, things moving), so every
// view keeps a few things moving that are not you and not a threat. They spawn just outside the view (nothing pops in), are
// depth-sorted with the world like a figure, and go once they are well off it.
import { Container, Graphics } from 'pixi.js';
import { CELL_PX, toScreen, fromScreen, type IsoMap } from '../../shared/src/iso';

interface Bird { g: Graphics; x: number; y: number; z: number; vx: number; vy: number; vz: number; fly: boolean; t: number; ph: number; land?: number }
interface Flock { birds: Bird[]; x: number; y: number }
interface Scrap { g: Graphics; x: number; y: number; z: number; vz: number; t: number; spin: number; col: number }

const FLOCKS = 3, SCRAPS = 6, FLUSH_R = 28, MARGIN = 60;   // (28 world px: 3.5 m)
const BIRD_COLS = [0x6e7079, 0x5d5f68, 0x81838c, 0x4f4a46];
const SCRAP_COLS = [0xd8d2c0, 0xb8b0a0, 0xc9b98a, 0x9aa6b0];

export class Ambient {
  flocks: Flock[] = [];
  scraps: Scrap[] = [];
  wind = { x: 1, y: 0.35 };
  constructor(private layer: () => Container, private map: () => IsoMap) {}

  /** an outdoor ground cell (no building, nothing standing in it) at world px x,y */
  private open(x: number, y: number) {
    const m = this.map(), cx = Math.floor(x / CELL_PX), cy = Math.floor(y / CELL_PX);
    if (!m.inside(cx, cy)) return false;
    const i = cx + cy * m.w; return !m.b[i] && !m.s[i] && !m.propAt[i];
  }
  /** open AND seen: no building on the 12 m of diagonal between it and the camera (a bird behind a roof is no bird) */
  private seen(x: number, y: number) {
    if (!this.open(x, y)) return false;
    const m = this.map(), cx = Math.floor(x / CELL_PX), cy = Math.floor(y / CELL_PX);
    for (let k = 1; k <= 24; k++) for (const [ox, oy] of [[k, k], [k, k - 1]]) { const X = cx + ox, Y = cy + oy; if (m.inside(X, Y) && m.b[X + Y * m.w]) return false; }
    return true;
  }
  /** a world point just outside the view (screen rect sx0..sx1, sy0..sy1 in world-screen px), on open ground */
  private edgePoint(v: number[]): [number, number] | null {
    const [sx0, sy0, sx1, sy1] = v;
    for (let k = 0; k < 20; k++) {
      const side = Math.floor(Math.random() * 4), t = Math.random();
      const sx = side === 0 ? sx0 - MARGIN * 0.6 : side === 1 ? sx1 + MARGIN * 0.6 : sx0 + (sx1 - sx0) * t;
      const sy = side === 2 ? sy0 - MARGIN * 0.6 : side === 3 ? sy1 + MARGIN * 0.6 : sy0 + (sy1 - sy0) * t;
      const [x, y] = fromScreen(sx, sy);
      if (this.seen(x, y)) return [x, y];
    }
    return null;
  }
  /** open ground inside the view, at least 9 m from everyone who would scare a bird */
  private innerPoint(v: number[], movers: [number, number][]): [number, number] | null {
    const [sx0, sy0, sx1, sy1] = v;
    for (let k = 0; k < 20; k++) {
      const [x, y] = fromScreen(sx0 + 20 + Math.random() * (sx1 - sx0 - 40), sy0 + 40 + Math.random() * (sy1 - sy0 - 80));
      if (this.seen(x, y) && !movers.some(([mx, my]) => Math.hypot(mx - x, my - y) < 72)) return [x, y];
    }
    return null;
  }
  private mkBird(x: number, y: number): Bird {
    const g = new Graphics(), col = BIRD_COLS[Math.floor(Math.random() * BIRD_COLS.length)];
    g.ellipse(0, -1.5, 2, 1.2).fill({ color: col }); g.circle(1.6, -2.6, 0.9).fill({ color: col - 0x101010 });
    g.rect(-2.6, -1.8, 1, 0.8).fill({ color: col - 0x181818 });
    const b: Bird = { g, x, y, z: 0, vx: 0, vy: 0, vz: 0, fly: false, t: Math.random() * 3, ph: Math.random() * 6 };
    if (Math.random() < 0.5) g.scale.x = -1;
    this.layer().addChild(g); return b;
  }
  private mkScrap(x: number, y: number): Scrap {
    const g = new Graphics(), col = SCRAP_COLS[Math.floor(Math.random() * SCRAP_COLS.length)];
    g.rect(-1.5, -1, 3, 2).fill({ color: col }); g.rect(-1.5, -1, 3, 0.6).fill({ color: 0xffffff, alpha: 0.25 });
    const s: Scrap = { g, x, y, z: 0, vz: 0, t: Math.random() * 2, spin: Math.random() * 6, col };
    this.layer().addChild(s.g); return s;
  }

  /** view: the visible world-screen rect [sx0, sy0, sx1, sy1]; movers: world px of everyone who scares a bird; on: the
   *  street is shown (not indoors, not underground); light 0..1 (birds roost at night) */
  update(dt: number, view: number[], movers: [number, number][], on: boolean, light: number) {
    const [sx0, sy0, sx1, sy1] = view;
    const offView = (x: number, y: number, z = 0) => { const [sx, sy] = toScreen(x, y, z / 8); return sx < sx0 - MARGIN * 2 || sx > sx1 + MARGIN * 2 || sy < sy0 - MARGIN * 2 || sy > sy1 + MARGIN * 2; };
    // keep the numbers up (birds by day; litter always), drop what has gone far off the view or the street is not shown
    if (on) {
      if (light > 0.35 && this.flocks.length < FLOCKS && Math.random() < dt * 0.8) {
        // a flock comes down out of the sky onto open ground in the view, away from everyone (it lands; it does not pop in)
        const p = this.innerPoint(view, movers);
        if (p) { const n = 3 + Math.floor(Math.random() * 4), f: Flock = { birds: [], x: p[0], y: p[1] };
          for (let i = 0; i < n; i++) { const bx = p[0] + (Math.random() - 0.5) * 14, by = p[1] + (Math.random() - 0.5) * 10; if (this.open(bx, by)) { const b = this.mkBird(bx, by); b.land = 1; b.z = 150 + Math.random() * 40; b.vx = -this.wind.x * 4; b.vy = -this.wind.y * 4; f.birds.push(b); } }
          if (f.birds.length) this.flocks.push(f); }
      }
      if (this.scraps.length < SCRAPS && Math.random() < dt * 1.2) { const p = this.edgePoint(view); if (p) this.scraps.push(this.mkScrap(p[0], p[1])); }
    }
    // the wind turns slowly
    const wa = Math.atan2(this.wind.y, this.wind.x) + (Math.random() - 0.5) * dt * 0.3; this.wind.x = Math.cos(wa); this.wind.y = Math.sin(wa);
    for (let fi = this.flocks.length - 1; fi >= 0; fi--) {
      const f = this.flocks[fi]; let alive = 0;
      for (const b of f.birds) {
        if (b.land) {                                // coming down: a glide, wings beating, then the last hop onto the ground
          b.z = Math.max(0, b.z - 70 * dt); b.x += b.vx * dt; b.y += b.vy * dt; b.ph += dt * 26; b.g.scale.y = b.z > 4 && Math.sin(b.ph) > 0 ? -0.6 : 1;
          if (b.z <= 0) { b.land = 0; b.g.scale.y = 1; }
        } else if (!b.fly) {
          // scared: someone close, or the light went
          const scare = movers.find(([mx, my]) => Math.hypot(mx - b.x, my - b.y) < FLUSH_R);
          if (scare || light < 0.25 || !on) {
            b.fly = true; const ax = b.x - (scare ? scare[0] : b.x - 1), ay = b.y - (scare ? scare[1] : b.y), l = Math.hypot(ax, ay) || 1;
            b.vx = ax / l * (40 + Math.random() * 20) + (Math.random() - 0.5) * 16; b.vy = ay / l * (40 + Math.random() * 20) + (Math.random() - 0.5) * 16; b.vz = 30 + Math.random() * 20;
            if (scare) for (const o of f.birds) if (!o.fly && Math.random() < 0.85) { o.t = -Math.random() * 0.25; }   // the rest go a beat later
          } else {
            // peck, bob, take a hop
            b.t += dt; if (b.t > 0.4 + Math.random() * 2) { b.t = 0; if (Math.random() < 0.35) { const nx = b.x + (Math.random() - 0.5) * 6, ny = b.y + (Math.random() - 0.5) * 4; if (this.open(nx, ny)) { b.g.scale.x = nx > b.x ? 1 : -1; b.x = nx; b.y = ny; } } }
            b.ph += dt * 9; b.g.rotation = Math.sin(b.ph) > 0.7 ? 0.35 * b.g.scale.x : 0;
          }
          if (b.t < 0) { b.t += dt; if (b.t >= 0) { b.fly = true; b.vx = (Math.random() - 0.5) * 80; b.vy = (Math.random() - 0.5) * 60; b.vz = 34 + Math.random() * 16; } }
        } else {
          b.x += b.vx * dt; b.y += b.vy * dt; b.z += b.vz * dt; b.vz += 6 * dt; b.ph += dt * 30;
          b.g.rotation = 0; b.g.scale.y = Math.sin(b.ph) > 0 ? 1 : -0.6;   // the wings beat
        }
        const [sx, sy] = toScreen(b.x, b.y, b.z / 8);
        b.g.position.set(Math.round(sx), Math.round(sy)); b.g.zIndex = b.fly ? b.x + b.y + 400 + b.z : b.x + b.y;
        const gone = (b.fly && b.z > 260) || offView(b.x, b.y, b.z);
        if (gone) { b.g.destroy(); (b as any).dead = true; } else alive++;
      }
      f.birds = f.birds.filter(b => !(b as any).dead);
      if (!alive) this.flocks.splice(fi, 1);
    }
    for (let i = this.scraps.length - 1; i >= 0; i--) {
      const s = this.scraps[i];
      // gusts: still a while, then a run of hops with the wind
      s.t -= dt;
      if (s.t < 0) { if (s.z <= 0 && Math.random() < dt * 3) s.vz = 6 + Math.random() * 10; if (s.t < -2.5) s.t = 0.5 + Math.random() * 3; }
      if (s.t < 0 || s.z > 0) {
        const nx = s.x + this.wind.x * 22 * dt, ny = s.y + this.wind.y * 22 * dt;
        if (this.open(nx, ny)) { s.x = nx; s.y = ny; } else s.t = 1 + Math.random() * 2;
        s.spin += dt * 9;
      }
      s.z = Math.max(0, s.z + s.vz * dt); s.vz -= 40 * dt; if (s.z === 0) s.vz = 0;
      const [sx, sy] = toScreen(s.x, s.y, s.z / 8);
      s.g.position.set(Math.round(sx), Math.round(sy)); s.g.rotation = Math.sin(s.spin) * 0.8; s.g.scale.y = 0.6 + 0.4 * Math.abs(Math.cos(s.spin));
      s.g.zIndex = s.x + s.y; s.g.visible = on;
      if (offView(s.x, s.y) || !on) { s.g.destroy(); this.scraps.splice(i, 1); }
    }
  }
  clear() { for (const f of this.flocks) for (const b of f.birds) b.g.destroy(); for (const s of this.scraps) s.g.destroy(); this.flocks = []; this.scraps = []; }
}
